<template>
  <v-app>
    <app-top-bar :class="hasBackgroundImage ? '' : 'bg-background'" :path-return="pathPage">{{ $t("modelList.pageTitle", { brand: brand }) }}</app-top-bar>

    <v-main :style="mainStyle" :class="hasBackgroundImage ? '' : 'bg-background'">
      <v-container v-if="showReportInfo">
        <v-sheet class="attention rounded-lg px-2 py-2 d-flex mb-5">
          <v-icon class="mr-1" color="#D82B18" icon="mdi-alert-circle-outline" />
          <div>
            <p class="font-weight-medium mb-0">
              {{ $t("modelList.reportInfoAttentionTitle") }}
            </p>
          </div>
        </v-sheet>

        <h1 class="text-on-surface-variant page-title mb-4">
          {{ $t("modelList.reportInfoTitle") }}
        </h1>

        <p class="text-body-1 mb-4">
          {{ $t("modelList.reportInfoSimulation") }}
        </p>

        <p class="text-body-1 mb-4">
          {{ $t("modelList.reportInfoNewPrice") }}
        </p>

        <p class="text-body-1 mb-4" v-html="reportInfoReviewText"></p>

        <div
          class="checkbox-section pa-2 rounded-lg custom-checkbox-container"
          :class="{ 'is-selected': consentChecked }"
          @click="toggleConsent"
        >
          <input
            type="checkbox"
            :checked="consentChecked"
            class="hidden-checkbox"
            tabindex="-1"
            aria-hidden="true"
          />
          <div class="custom-checkbox-ui">
            <span class="custom-checkbox-box">
              <span class="custom-checkbox-check"></span>
            </span>
            <label class="custom-checkbox-label">
              {{ $t("modelList.reportInfoConsentLabel") }}
            </label>
          </div>
        </div>
      </v-container>

      <v-container v-if="!showComplete && !showReportInfo">
        <p
          class="font-montserrat text-h7 mb-6 text-center text-on-surface-variant"
        >
          {{ $t("modelList.description") }}
        </p>

        <v-text-field
          v-model.trim="search"
          class="mb-1 text-on-surface-variant"
          variant="underlined"
          :label="$t('modelList.searchLabel')"
          type="text"
          color="primary"
          background-color="surface"
        />

        <v-sheet
          v-for="bike in filteredBikes"
          :key="bike.id"
          class="d-flex flex-column justify-center bg-background"
          @click="onSelectBike(bike.id)"
          :style="{ opacity: isLoading || isAdvancing ? 0.5 : 1, pointerEvents: isLoading || isAdvancing ? 'none' : 'auto' }"
        >
          <v-img
            contain
            :src="util.bikeImageUrl(bike)"
            :v-image-fallback="util.transpPixel()"
          >
            <template #placeholder>
              <div class="d-flex align-center justify-center fill-height">
                <v-progress-circular color="grey-lighten-4" indeterminate />
              </div>
            </template>
          </v-img>

          <p class="font-weight-bold text-center mt-2 px-10 text-on-surface-variant">
            {{ bike.model }}
          </p>
        </v-sheet>

        <v-btn
          v-if="showNotFoundButton"
          block
          color="primary"
          variant="outlined"
          class="mt-4 mb-6"
          @click="openNotFoundModal"
        >
          {{ $t("modelList.notFoundButton") }}
        </v-btn>

        <v-overlay :model-value="isLoading || isAdvancing" class="align-center justify-center">
            <v-progress-circular color="primary" indeterminate size="64"></v-progress-circular>
        </v-overlay>
      </v-container>
      <v-container v-if="showComplete">
        <phase-completed />
      </v-container>
    </v-main>

    <app-footer v-if="showComplete">
      <continue-footer-button :loading="isAdvancing" @click="advanceFlow" />
    </app-footer>

    <app-footer v-if="showReportInfo">
      <footer-button
        color="primary"
        :loading="isAdvancing"
        :disabled="!consentChecked"
        @click="confirmReportInfo"
      >
        {{ $t("modelList.reportInfoContinueButton") }}
      </footer-button>
    </app-footer>

    <not-found-model-dialog
      v-model="isNotFoundModalVisible"
      @select-bike="onSelectBike"
      @reported="onReported"
    />
  </v-app>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref } from "vue";

import { useI18n } from "vue-i18n";

import NotFoundModelDialog from "@/components/NotFoundModel/NotFoundModelDialog.vue";
import { useApiClient } from "@/composables/useApiClient";
import { usePageBackground } from "@/composables/usePageBackground";
import { useStepFlow } from "@/composables/useStepFlow";
import { useUtils } from "@/composables/useUtils";
import { useApiStore } from "@/stores/api";
import { useAppStore } from "@/stores/app";

const NOT_FOUND_BUTTON_DELAY_MS = 15_000;
const NOT_FOUND_AUTO_OPEN_DELAY_MS = 30_000;

const { t } = useI18n();
const api = useApiStore();
const app = useAppStore();
const apiClient = useApiClient();
const { mainStyle, hasBackgroundImage } = usePageBackground();
const util = useUtils();
const search = ref("");
const isLoading = ref(false);
const pathPage = '/bike-model';
const withinBusinessHours = ref(true);
const businessHours = ref<{ startTime: string; endTime: string; weekdaysLabel: string } | null>(null);

const { isAdvancing, showComplete, advance: advanceFlow } = useStepFlow(pathPage, () => isComplete.value);
const filteredBikes = computed(() => api.bikes.filter((b) => b.model.toLowerCase().includes(search.value.toLowerCase())));
const brand = computed(() => api.bikes[0]?.brand ?? useAppStore().capitalizedBrand);
const isComplete = computed<boolean>(() => api.isItemComplete("finished"));

const isNotFoundModalVisible = ref(false);
const showNotFoundButton = ref(false);
const showReportInfo = ref(false);
const consentChecked = ref(false);
let notFoundButtonTimer: ReturnType<typeof setTimeout> | null = null;
let notFoundAutoOpenTimer: ReturnType<typeof setTimeout> | null = null;

function openNotFoundModal() {
  isNotFoundModalVisible.value = true;
}

function onReported() {
  consentChecked.value = false;
  showReportInfo.value = true;

  apiClient.getBusinessHours().then((res) => {
    withinBusinessHours.value = res.data.withinBusinessHours;
    businessHours.value = {
      startTime: res.data.startTime,
      endTime: res.data.endTime,
      weekdaysLabel: res.data.weekdaysLabel,
    };
  });
}

const reportInfoReviewText = computed(() => {
  if (withinBusinessHours.value || !businessHours.value) {
    return t("modelList.reportInfoReview");
  }
  return t("modelList.reportInfoReviewOutOfHours", {
    weekdays: businessHours.value.weekdaysLabel,
    start: businessHours.value.startTime,
    end: businessHours.value.endTime,
  });
});

function toggleConsent() {
  consentChecked.value = !consentChecked.value;
}

async function confirmReportInfo() {
  try {
    await advanceFlow();
  } catch (error) {
    console.error(error);
  }
}

async function advance() {
  try {
    await advanceFlow(async () => {
      await api.setBike(api.bike.id);
    });
  } catch (error) {
    console.error(error);
  }
}

function onSelectBike(bikeId: number) {
  if (isLoading.value || isAdvancing.value) {
    return;
  }
  api.bike = {
    ...api.bike,
    id: bikeId,
  };
  advance();
}

onMounted(async () => {
  isLoading.value = true;
  try {
    await Promise.all([api.listBikes()]);
  } finally {
    isLoading.value = false;
  }

  notFoundButtonTimer = setTimeout(() => {
    showNotFoundButton.value = true;
  }, NOT_FOUND_BUTTON_DELAY_MS);

  notFoundAutoOpenTimer = setTimeout(() => {
    if (!isNotFoundModalVisible.value) {
      isNotFoundModalVisible.value = true;
    }
  }, NOT_FOUND_AUTO_OPEN_DELAY_MS);
});

onBeforeUnmount(() => {
  if (notFoundButtonTimer) clearTimeout(notFoundButtonTimer);
  if (notFoundAutoOpenTimer) clearTimeout(notFoundAutoOpenTimer);
});
</script>

<style scoped>
.attention {
  background-color: #ffcbc2;
}

.checkbox-section {
  background-color: #efefef;
  cursor: pointer;
}

.custom-checkbox-container {
  display: flex;
  align-items: center;
  user-select: none;
}

.hidden-checkbox {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

.custom-checkbox-ui {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.custom-checkbox-box {
  width: 20px;
  height: 20px;
  border: 2px solid #9e9e9e;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  transition: all 0.2s ease-in-out;
}

.custom-checkbox-check {
  width: 6px;
  height: 11px;
  border: solid transparent;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
  transition: all 0.2s ease-in-out;
}

.custom-checkbox-label {
  white-space: normal;
  word-break: break-word;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
}

.is-selected .custom-checkbox-box {
  border-color: #f44336;
  background-color: #f44336;
}

.is-selected .custom-checkbox-check {
  border-color: #fff;
}

.custom-checkbox-container:hover .custom-checkbox-box {
  box-shadow: 0 0 0 4px rgba(244, 67, 54, 0.1);
}
</style>
